/**
 * Procedural sound engine — zero audio files. Every sound is synthesized with Web Audio at play time.
 * Deterministic parameters (no Math.random in tone shapes) so the video factory renders the same audio.
 * Audio context is created lazily on the first user gesture (browser autoplay policy).
 */
type Ctx = AudioContext;

const MUTE_KEY = "mozaik.muted";
let ctx: Ctx | null = null;
let master: GainNode | null = null;
let muted = false;
try {
  muted = localStorage.getItem(MUTE_KEY) === "1";
} catch {
  /* ignore */
}

function ensure(): Ctx | null {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : 0.6;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Call from the first pointerdown so the context is unlocked before sounds are needed. */
export function unlockAudio(): void {
  ensure();
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(m: boolean): void {
  muted = m;
  try {
    localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (master && ctx) master.gain.setTargetAtTime(m ? 0 : 0.6, ctx.currentTime, 0.02);
}

interface ToneOpts {
  type?: OscillatorType;
  freq: number;
  /** end frequency for a glide */
  to?: number;
  dur: number;
  gain?: number;
  attack?: number;
  delay?: number;
  /** lowpass cutoff */
  lp?: number;
}

function tone(o: ToneOpts): void {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const osc = c.createOscillator();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t0 + o.dur);
  const g = c.createGain();
  const a = o.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.3, t0 + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
  let node: AudioNode = osc;
  if (o.lp) {
    const f = c.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = o.lp;
    osc.connect(f);
    node = f;
  }
  node.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + o.dur + 0.02);
}

/** Short filtered noise burst (impacts, whooshes). */
function noise(dur: number, gain = 0.25, lp = 1200, delay = 0, hp = 0): void {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + delay;
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  // Deterministic pseudo-noise (LCG) so renders are reproducible.
  let s = 12345;
  for (let i = 0; i < len; i++) {
    s = (s * 1664525 + 1013904223) >>> 0;
    d[i] = (s / 4294967296) * 2 - 1;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = "lowpass";
  f.frequency.setValueAtTime(lp, t0);
  f.frequency.exponentialRampToValueAtTime(Math.max(80, lp * 0.15), t0 + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  let node: AudioNode = f;
  if (hp) {
    const h = c.createBiquadFilter();
    h.type = "highpass";
    h.frequency.value = hp;
    f.connect(h);
    node = h;
  }
  src.connect(f);
  node.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

const PENTA = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21];
const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

export const sfx = {
  /** Invalid tap: soft, low "nope". */
  nope(): void {
    tone({ type: "triangle", freq: 220, to: 180, dur: 0.08, gain: 0.12 });
  },
  /** Group cleared: pitch rises with group size; bigger groups get a chord. */
  pop(size: number): void {
    const step = Math.min(PENTA.length - 1, Math.max(0, size - 2));
    const base = 60 + PENTA[step]!;
    tone({ type: "triangle", freq: midi(base), to: midi(base) * 1.5, dur: 0.12, gain: 0.25 });
    noise(0.06, 0.12, 2500);
    if (size >= 5) tone({ type: "sine", freq: midi(base + 7), dur: 0.18, gain: 0.15, delay: 0.03 });
    if (size >= 8) tone({ type: "sine", freq: midi(base + 12), dur: 0.22, gain: 0.12, delay: 0.06 });
  },
  rocket(): void {
    noise(0.28, 0.3, 4000, 0, 300);
    tone({ type: "sawtooth", freq: 300, to: 1400, dur: 0.25, gain: 0.08, lp: 1800 });
  },
  bomb(): void {
    tone({ type: "sine", freq: 120, to: 35, dur: 0.45, gain: 0.5 });
    noise(0.35, 0.35, 900);
  },
  special(): void {
    // sparkle when a rocket/bomb is created
    [0, 4, 7, 12].forEach((n, i) => tone({ type: "sine", freq: midi(84 + n), dur: 0.14, gain: 0.1, delay: i * 0.04 }));
  },
  goalTick(): void {
    tone({ type: "square", freq: 1200, dur: 0.04, gain: 0.05, lp: 3000 });
  },
  win(): void {
    [0, 4, 7, 12, 16].forEach((n, i) => tone({ type: "triangle", freq: midi(72 + n), dur: 0.35, gain: 0.18, delay: i * 0.09 }));
    noise(0.5, 0.12, 6000, 0.3, 1500);
  },
  lose(): void {
    [7, 3, 0].forEach((n, i) => tone({ type: "triangle", freq: midi(60 + n), dur: 0.3, gain: 0.15, delay: i * 0.16 }));
  },
  shuffle(): void {
    for (let i = 0; i < 6; i++) tone({ type: "triangle", freq: midi(64 + (i % 3) * 3), dur: 0.06, gain: 0.08, delay: i * 0.05 });
  },
  reveal(): void {
    tone({ type: "sine", freq: midi(96), dur: 0.5, gain: 0.08 });
    tone({ type: "sine", freq: midi(103), dur: 0.6, gain: 0.06, delay: 0.1 });
  },
};
